import React, { FC, useContext } from "react";
import {
  system,
  SelectProps as FlicketSelectProps,
  getColorContrast,
} from "flicket-ui";
import ReactSelect, { components } from "react-select";
import styled, { css, DefaultTheme, ThemeContext } from "styled-components";

import { Icon } from "~components/common/Icon";

type StyledSelectProps = FlicketSelectProps & {
  small: boolean;
};

const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <Icon
        className="chevron"
        icon="arrow-dropdown"
        fontSize={props?.small ? 2 : 3}
        mr={2}
      />
    </components.DropdownIndicator>
  );
};

const StyledSelect = styled(ReactSelect)<StyledSelectProps>(
  ({ styledTheme: theme, small }) => css`
    font-size: ${small ? "14px" : "16px"};
    font-weight: ${small
      ? theme.fontWeights.demiBold
      : theme.fontWeights.medium};
    line-height: 100%;

    ${system}

    .react-select {
      &__placeholder {
        color: ${theme.colors.N500};
        margin: 0;
        font-weight: ${theme.fontWeights.medium};
      }

      &__input {
        padding: -2px 0;
        height: 0;
      }

      &__control {
        transition: border-color 0.18s ease;
        border-radius: ${theme.radii.sm};
        box-shadow: ${theme.shadows.inner.sm};
        border-color: ${theme.colors.N200};
        min-height: ${small ? "32px" : "48px"};

        &:hover {
          border-color: ${theme.colors.N300};
        }

        .react-select__dropdown-indicator {
          padding: ${small ? 0 : "8px"};

          .chevron {
            margin-right: 8px;
            transition: all 0.25s cubic-bezier(0.09, 1.08, 0.77, 1.18);
          }

          svg {
            color: ${theme.colors.P300};
          }
        }

        &--menu-is-open {
          .react-select__dropdown-indicator {
            .chevron {
              transform: rotate(180deg);
            }
          }
        }

        &--is-disabled {
          background: ${theme.colors.N100};
          color: ${theme.colors.N500};

          .react-select__placeholder {
            color: ${theme.colors.N500};
          }
        }
      }

      &__option {
        padding: ${small ? "6px" : "8px"};
        margin: 0 8px;
        width: calc(100% - 16px);
        line-height: 1;
        border-radius: ${theme.radii.xs};
        font-size: 16px;
        font-weight: ${theme.fontWeights.medium};
        color: ${theme.colors.N800};

        &--is-focused,
        &:active {
          background-color: ${theme.colors.N100};

          &:active {
            background-color: ${theme.colors.N200};
          }
        }

        &--is-selected {
          background-color: ${theme.colors.P300};
          color: ${theme.colors["P300.contrast-color"]};
        }
      }

      &__menu {
        border: none;
        border-radius: ${theme.radii.sm};
        box-shadow: ${theme.shadows.hover};
        margin-top: 4px;

        &-list {
          padding-top: 8px;
          padding-bottom: 8px;
        }
      }

      &__value-container {
        padding: ${small ? "0 8px 8px 8px " : "8px 8px 8px 16px"};
      }

      &__indicator-separator {
        display: none;
      }
    }
  `
);

interface SelectProps extends FlicketSelectProps {
  theme?: any;
  small?: boolean;
}

export const Select: FC<SelectProps> = (props) => {
  const styledTheme = useContext(ThemeContext); // react-select and styled-components both need a theme so it needs to be renamed

  return (
    <StyledSelect
      components={{ DropdownIndicator }}
      className="react-select"
      classNamePrefix="react-select"
      styledTheme={styledTheme}
      theme={(theme: DefaultTheme) => ({
        ...theme,
        colors: {
          ...theme.colors,
          primary: styledTheme.colors.P100,
          primary25: styledTheme.colors.P100,
          primary50: styledTheme.colors.P100,
        },
      })}
      {...props}
    />
  );
};

export default Select;
